import { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, ChevronDown, Filter, X, Grid, List, ArrowUpDown, Tag, ChevronLeft, ChevronRight } from 'lucide-react';
import { supabase } from '../lib/supabase';
import SEO from '../components/SEO';
import { useProductPrices } from '../hooks/useProductPrices';
import { ProxyImage } from '../lib/imageProxy';
import { ProductGridSkeleton } from '../components/ui/Skeleton';
import { ProductCard } from '../components/product/ProductCard';

interface Product {
  id: string;
  name: string;
  description: string;
  base_price: number;
  main_image: string;
  stock: number;
  min_order: number;
  status: 'active' | 'inactive' | 'out_of_stock';
  categories?: { name: string; slug: string; id: string; }[];
  prices?: {
    min_quantity: number;
    price: number;
    offer_price?: number | null;
  }[];
  code?: string;
}

interface Category {
  id: string;
  name: string;
  slug: string;
  description: string;
  image: string;
  parent_id: string | null;
  product_count?: number;
}

const sortOptions = [
  { label: 'Más relevantes', value: 'relevance' },
  { label: 'Menor precio', value: 'price_asc' },
  { label: 'Mayor precio', value: 'price_desc' },
  { label: 'Más vendidos', value: 'sales' },
  { label: 'Más nuevos', value: 'newest' },
];

const priceRanges = [
  { label: 'Menos de Q100', min: 0, max: 100 },
  { label: 'Q100 - Q500', min: 100, max: 500 },
  { label: 'Q500 - Q1000', min: 500, max: 1000 },
  { label: 'Más de Q1000', min: 1000, max: null },
];

const PRODUCTS_PER_PAGE = 24;

export default function Catalog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [showSortOptions, setShowSortOptions] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const sortRef = useRef<HTMLDivElement>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Obtener IDs de productos para el hook de precios
  const productIds = useMemo(() => products.map(p => p.id), [products]);

  // Usar el hook centralizado de precios
  const { prices, loading: pricesLoading } = useProductPrices(productIds, {
    enabled: productIds.length > 0
  });

  const currentSort = searchParams.get('sort') || 'relevance';
  const currentPriceRange = searchParams.get('price_range');
  const urlSearchQuery = searchParams.get('q') || '';
  const categorySlug = searchParams.get('category') || '';
  const inStockOnly = searchParams.get('in_stock') === 'true';
  const page = parseInt(searchParams.get('page') || '1');

  // Sincronizar currentPage con URL
  useEffect(() => {
    setCurrentPage(page);
    setSearchQuery(urlSearchQuery);
  }, [page, urlSearchQuery]);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [currentSort, currentPriceRange, urlSearchQuery, categorySlug, inStockOnly, currentPage]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(event.target as Node)) {
        setShowSortOptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .is('parent_id', null)
        .order('order', { ascending: true });

      if (error) throw error;

      // Contar productos por categoría
      const categoriesWithCount = await Promise.all(
        (data || []).map(async (cat) => {
          const { count } = await supabase
            .from('product_categories')
            .select('*', { count: 'exact', head: true })
            .eq('category_id', cat.id);

          return {
            ...cat,
            product_count: count || 0
          };
        })
      );

      setCategories(categoriesWithCount);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);

      const from = (currentPage - 1) * PRODUCTS_PER_PAGE;
      const to = from + PRODUCTS_PER_PAGE - 1;

      if (urlSearchQuery) {
        const { data, error } = await supabase.rpc('search_products_advanced', {
          search_query: urlSearchQuery,
          p_category_id: categorySlug ? await getCategoryIdBySlug(categorySlug) : null,
          min_price: currentPriceRange ? parseFloat(currentPriceRange.split('-')[0]) : null,
          max_price: currentPriceRange ? parseFloat(currentPriceRange.split('-')[1]) : null,
          in_stock: inStockOnly,
          on_sale_only: false,
          company_filter: [],
          limit_count: PRODUCTS_PER_PAGE,
          offset_count: from
        });

        if (error) throw error;

        const productsWithCategories = await Promise.all(
          (data || []).map(async (product: any) => {
            const { data: categoryData } = await supabase
              .from('product_categories')
              .select('category:categories(id, name, slug)')
              .eq('product_id', product.id);

            return {
              ...product,
              categories: categoryData?.map((pc: any) => pc.category) || []
            };
          })
        );

        setProducts(productsWithCategories);
        setTotalProducts(data?.length || 0);
        setTotalPages(Math.ceil((data?.length || 0) / PRODUCTS_PER_PAGE));
        return;
      }

      let query = supabase
        .from('products')
        .select(`
          id,
          name,
          description,
          base_price,
          main_image,
          stock,
          min_order,
          status,
          code
        `, { count: 'exact' })
        .eq('status', 'active');

      if (categorySlug) {
        const categoryId = await getCategoryIdBySlug(categorySlug);
        if (categoryId) {
          const { data: productIds } = await supabase
            .from('product_categories')
            .select('product_id')
            .eq('category_id', categoryId);

          const ids = productIds?.map(pc => pc.product_id) || [];
          if (ids.length > 0) {
            query = query.in('id', ids);
          } else {
            setProducts([]);
            setTotalProducts(0);
            setTotalPages(0);
            setLoading(false);
            return;
          }
        }
      }

      if (currentPriceRange) {
        const [min, max] = currentPriceRange.split('-').map(Number);
        query = query.gte('base_price', min);
        if (max) query = query.lt('base_price', max);
      }

      if (inStockOnly) {
        query = query.gt('stock', 0);
      }

      switch (currentSort) {
        case 'price_asc':
          query = query.order('base_price', { ascending: true });
          break;
        case 'price_desc':
          query = query.order('base_price', { ascending: false });
          break;
        case 'newest':
          query = query.order('created_at', { ascending: false });
          break;
        default:
          query = query.order('created_at', { ascending: false });
      }

      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) throw error;

      const productsWithCategories = await Promise.all(
        (data || []).map(async (product: any) => {
          const { data: categoryData } = await supabase
            .from('product_categories')
            .select('category:categories(id, name, slug)')
            .eq('product_id', product.id);

          return {
            ...product,
            categories: categoryData?.map((pc: any) => pc.category) || []
          };
        })
      );

      setProducts(productsWithCategories);
      setTotalProducts(count || 0);
      setTotalPages(Math.ceil((count || 0) / PRODUCTS_PER_PAGE));

    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIdBySlug = async (slug: string): Promise<string | null> => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('id')
        .eq('slug', slug)
        .single();

      if (error) return null;
      return data?.id || null;
    } catch {
      return null;
    }
  };

  const updateSort = (value: string) => {
    searchParams.set('sort', value);
    searchParams.set('page', '1');
    setSearchParams(searchParams);
    setShowSortOptions(false);
  };

  const updatePriceRange = (range: string) => {
    searchParams.set('price_range', range);
    searchParams.set('page', '1');
    setSearchParams(searchParams);
  };

  const toggleInStock = () => {
    if (inStockOnly) {
      searchParams.delete('in_stock');
    } else {
      searchParams.set('in_stock', 'true');
    }
    searchParams.set('page', '1');
    setSearchParams(searchParams);
  };

  const clearFilters = () => {
    const newParams = new URLSearchParams();
    if (urlSearchQuery) newParams.set('q', urlSearchQuery);
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const goToPage = (page: number) => {
    searchParams.set('page', page.toString());
    setSearchParams(searchParams);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    searchParams.set('q', searchQuery);
    searchParams.set('page', '1');
    setSearchParams(searchParams);
  };

  const selectCategory = (slug: string) => {
    if (categorySlug === slug) {
      searchParams.delete('category');
    } else {
      searchParams.set('category', slug);
    }
    searchParams.set('page', '1');
    setSearchParams(searchParams);
    setShowFilters(false);
  };

  const hasActiveFilters = currentPriceRange || inStockOnly || categorySlug;

  const getSortLabel = () => {
    const option = sortOptions.find(opt => opt.value === currentSort);
    return option ? option.label : 'Ordenar por';
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }

    return pages;
  };

  const selectedCategory = useMemo(() => {
    return categories.find(c => c.slug === categorySlug);
  }, [categories, categorySlug]);

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white min-h-screen">
      <SEO
        title="Catálogo de Productos"
        description="Explora nuestro catálogo completo de productos mayoristas. Encuentra peluches, tarjetas, globos y artículos para regalo al mejor precio en Guatemala."
        keywords={['catálogo', 'productos mayoristas', 'peluches', 'tarjetas', 'globos', 'regalos', 'Guatemala', 'precios mayoristas']}
        url="/catalog"
        type="website"
      />

      {/* Hero Banner Moderno */}
      <div className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white overflow-hidden">
        {/* Patrón de fondo decorativo */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 80%, white 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 py-16">
          
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Catálogo de Productos</h1>
          

          {/* Barra de búsqueda prominente */}
          <form onSubmit={handleSearchSubmit} className="max-w-2xl">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar por nombre, código o descripción..."
                className="w-full px-6 py-4 pr-14 rounded-full text-gray-900 text-lg focus:outline-none focus:ring-4 focus:ring-white/30 shadow-2xl"
              />
              <button
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full transition-colors"
              >
                <Search className="w-5 h-5" />
              </button>
            </div>
          </form>
        </div>

        {/* Onda decorativa */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V120Z" fill="white"/>
          </svg>
        </div>
      </div>

      {/* Categorías destacadas */}
      {!categorySlug && categories.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 -mt-8 mb-12 relative z-10">
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <span className="w-1 h-8 bg-blue-600 rounded-full"></span>
              Categorías Populares
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {categories.slice(0, 6).map((category) => (
                <button
                  key={category.id}
                  onClick={() => selectCategory(category.slug)}
                  className="group relative overflow-hidden rounded-xl aspect-square hover:scale-105 transition-transform duration-300"
                >
                  <ProxyImage
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent flex flex-col items-center justify-end p-4">
                    <h3 className="text-white font-semibold text-sm text-center mb-1">{category.name}</h3>
                    <span className="text-white/80 text-xs">{category.product_count} productos</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Mobile Filters Drawer */}
      {showFilters && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 md:hidden">
          <div className="absolute right-0 top-0 h-full w-80 bg-white shadow-lg overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
              <h3 className="font-semibold text-lg">Filtros</h3>
              <button
                onClick={() => setShowFilters(false)}
                className="p-2 rounded-full hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-6">
              {/* Categorías */}
              <div>
                <h4 className="font-medium mb-3 text-gray-900">Categorías</h4>
                <div className="space-y-2">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => selectCategory(category.slug)}
                      className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                        categorySlug === category.slug
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm">{category.name}</span>
                        <span className="text-xs opacity-70">({category.product_count})</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Rango de precios */}
              <div>
                <h4 className="font-medium mb-3 text-gray-900">Rango de precios</h4>
                <div className="space-y-2">
                  {priceRanges.map((range) => (
                    <label key={range.label} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="price_range"
                        value={`${range.min}-${range.max}`}
                        checked={currentPriceRange === `${range.min}-${range.max}`}
                        onChange={(e) => updatePriceRange(e.target.value)}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {range.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Disponibilidad */}
              <div>
                <h4 className="font-medium mb-3 text-gray-900">Disponibilidad</h4>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={inStockOnly}
                    onChange={toggleInStock}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Solo productos en stock
                  </span>
                </label>
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <button
                  onClick={clearFilters}
                  className="flex-1 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50"
                >
                  Limpiar
                </button>
                <button
                  onClick={() => setShowFilters(false)}
                  className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                >
                  Ver resultados
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar - Desktop */}
          <aside className="hidden lg:block w-72 flex-shrink-0">
            <div className="sticky top-4 space-y-6">
              {/* Categorías */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <div className="w-1 h-6 bg-blue-600 rounded-full"></div>
                  Categorías
                </h3>
                <div className="space-y-2">
                  <button
                    onClick={() => {
                      searchParams.delete('category');
                      searchParams.set('page', '1');
                      setSearchParams(searchParams);
                    }}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      !categorySlug
                        ? 'bg-blue-600 text-white font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Todas las categorías</span>
                    </div>
                  </button>
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => selectCategory(category.slug)}
                      className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                        categorySlug === category.slug
                          ? 'bg-blue-600 text-white font-medium'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm">{category.name}</span>
                        <span className="text-xs opacity-70">({category.product_count})</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Filtros */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <Filter className="w-5 h-5 text-blue-600" />
                    Filtros
                  </h3>
                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    >
                      <X className="w-3 h-3" />
                      Limpiar
                    </button>
                  )}
                </div>

                <div className="space-y-6">
                  {/* Rango de precios */}
                  <div>
                    <h4 className="font-medium mb-3 text-gray-700 text-sm">Rango de precios</h4>
                    <div className="space-y-2">
                      {priceRanges.map((range) => (
                        <label key={range.label} className="flex items-center cursor-pointer group">
                          <input
                            type="radio"
                            name="price_range"
                            value={`${range.min}-${range.max}`}
                            checked={currentPriceRange === `${range.min}-${range.max}`}
                            onChange={(e) => updatePriceRange(e.target.value)}
                            className="text-blue-600 focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-gray-600 group-hover:text-gray-900">
                            {range.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Disponibilidad */}
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-3 text-gray-700 text-sm">Disponibilidad</h4>
                    <label className="flex items-center cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={inStockOnly}
                        onChange={toggleInStock}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-600 group-hover:text-gray-900">
                        Solo productos en stock
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {/* Breadcrumb de categoría seleccionada */}
            {selectedCategory && (
              <div className="mb-6 flex items-center gap-2 text-sm">
                <button
                  onClick={() => {
                    searchParams.delete('category');
                    searchParams.set('page', '1');
                    setSearchParams(searchParams);
                  }}
                  className="text-blue-600 hover:text-blue-800"
                >
                  Todas las categorías
                </button>
                <ChevronRight className="w-4 h-4 text-gray-400" />
                <span className="font-medium text-gray-900">{selectedCategory.name}</span>
              </div>
            )}

            {/* Toolbar */}
            <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="lg:hidden flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
                >
                  <Filter className="w-5 h-5" />
                  Filtros
                  {hasActiveFilters && (
                    <span className="bg-blue-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                      !
                    </span>
                  )}
                </button>

                <div className="flex items-center gap-2 flex-1">
                  <div className="relative flex-1" ref={sortRef}>
                    <button
                      onClick={() => setShowSortOptions(!showSortOptions)}
                      className="w-full flex items-center justify-between gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
                    >
                      <div className="flex items-center gap-2">
                        <ArrowUpDown className="w-5 h-5 text-gray-400" />
                        <span className="text-sm">{getSortLabel()}</span>
                      </div>
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    {showSortOptions && (
                      <div className="absolute left-0 right-0 mt-2 bg-white rounded-lg shadow-xl py-2 z-20 border border-gray-200">
                        {sortOptions.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => updateSort(option.value)}
                            className={`w-full px-4 py-2 text-left text-sm transition-colors ${
                              currentSort === option.value
                                ? 'text-blue-600 bg-blue-50 font-medium'
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="hidden sm:flex border border-gray-300 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setView('grid')}
                      className={`p-2 transition-colors ${
                        view === 'grid' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <Grid className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setView('list')}
                      className={`p-2 transition-colors ${
                        view === 'list' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <List className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Active Filters Tags */}
              {hasActiveFilters && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {categorySlug && (
                    <div className="bg-blue-50 text-blue-700 text-sm px-3 py-1 rounded-full flex items-center gap-2">
                      <Tag className="w-3 h-3" />
                      {selectedCategory?.name}
                      <button
                        onClick={() => {
                          searchParams.delete('category');
                          searchParams.set('page', '1');
                          setSearchParams(searchParams);
                        }}
                        className="text-blue-500 hover:text-blue-700"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                  {currentPriceRange && (
                    <div className="bg-blue-50 text-blue-700 text-sm px-3 py-1 rounded-full flex items-center gap-2">
                      <Tag className="w-3 h-3" />
                      {priceRanges.find(r => `${r.min}-${r.max}` === currentPriceRange)?.label}
                      <button
                        onClick={() => {
                          searchParams.delete('price_range');
                          searchParams.set('page', '1');
                          setSearchParams(searchParams);
                        }}
                        className="text-blue-500 hover:text-blue-700"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                  {inStockOnly && (
                    <div className="bg-blue-50 text-blue-700 text-sm px-3 py-1 rounded-full flex items-center gap-2">
                      <Tag className="w-3 h-3" />
                      Solo en stock
                      <button
                        onClick={toggleInStock}
                        className="text-blue-500 hover:text-blue-700"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Results Count */}
            {!loading && (
              <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <p className="text-sm text-gray-600">
                  {urlSearchQuery ? (
                    <>
                      Resultados para <span className="font-semibold text-gray-900">"{urlSearchQuery}"</span>: {totalProducts} producto{totalProducts !== 1 ? 's' : ''}
                    </>
                  ) : (
                    <>
                      Mostrando <span className="font-semibold text-gray-900">{((currentPage - 1) * PRODUCTS_PER_PAGE) + 1} - {Math.min(currentPage * PRODUCTS_PER_PAGE, totalProducts)}</span> de <span className="font-semibold text-gray-900">{totalProducts}</span> productos
                    </>
                  )}
                </p>
                {totalPages > 1 && (
                  <p className="text-sm text-gray-600">
                    Página <span className="font-semibold text-gray-900">{currentPage}</span> de <span className="font-semibold text-gray-900">{totalPages}</span>
                  </p>
                )}
              </div>
            )}

            {/* Products Grid/List */}
            {loading ? (
              <div className={view === 'grid' ? 'grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-6' : 'space-y-4'}>
                {[...Array(PRODUCTS_PER_PAGE)].map((_, i) => (
                  <div key={i} className="bg-white rounded-xl overflow-hidden shadow-sm animate-pulse">
                    <div className="bg-gray-200 h-64"></div>
                    <div className="p-4 space-y-3">
                      <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                      <div className="h-10 bg-gray-200 rounded w-full mt-4"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-xl shadow-sm">
                <div className="text-gray-300 mb-4">
                  <Search className="w-20 h-20 mx-auto" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-800 mb-2">No se encontraron productos</h3>
                <p className="text-gray-500 mb-6">
                  {urlSearchQuery ? (
                    <>No encontramos productos que coincidan con "{urlSearchQuery}"</>
                  ) : (
                    <>Intenta ajustar los filtros de búsqueda</>
                  )}
                </p>
                <button
                  onClick={clearFilters}
                  className="px-8 py-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 font-medium shadow-lg hover:shadow-xl transition-all"
                >
                  Limpiar filtros
                </button>
              </div>
            ) : view === 'grid' ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-6">
                {products.map((product) => {
                  const priceData = prices[product.id];

                  return (
                    <ProductCard
                      key={product.id}
                      product={{
                        id: product.id,
                        name: product.name,
                        main_image: product.main_image,
                        stock: product.stock,
                        min_order: product.min_order,
                        code: product.code,
                        category: product.categories?.[0]
                      }}
                      priceInfo={priceData ? {
                        price: priceData.isOnSale && priceData.offerPrice ? priceData.offerPrice : priceData.price,
                        originalPrice: priceData.isOnSale ? priceData.price : null,
                        isOnSale: priceData.isOnSale,
                        discountPercentage: priceData.discountPercentage
                      } : undefined}
                      variant="catalog"
                      showCategory={true}
                      showCode={true}
                      showAddToCart={true}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="space-y-4">
                {products.map((product) => {
                  const priceData = prices[product.id];

                  return (
                    <ProductCard
                      key={product.id}
                      product={{
                        id: product.id,
                        name: product.name,
                        description: product.description,
                        main_image: product.main_image,
                        stock: product.stock,
                        min_order: product.min_order,
                        code: product.code,
                        category: product.categories?.[0]
                      }}
                      priceInfo={priceData ? {
                        price: priceData.isOnSale && priceData.offerPrice ? priceData.offerPrice : priceData.price,
                        originalPrice: priceData.isOnSale ? priceData.price : null,
                        isOnSale: priceData.isOnSale,
                        discountPercentage: priceData.discountPercentage
                      } : undefined}
                      variant="list"
                      showCategory={true}
                      showCode={true}
                      showAddToCart={true}
                    />
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {!loading && products.length > 0 && totalPages > 1 && (
              <div className="mt-12 flex flex-col items-center space-y-4">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border rounded-lg transition-all ${
                      currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600'
                    }`}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Anterior
                  </button>

                  <div className="hidden sm:flex items-center space-x-1">
                    {getPageNumbers().map((page, index) => (
                      <span key={index}>
                        {page === '...' ? (
                          <span className="px-3 py-2 text-gray-400">...</span>
                        ) : (
                          <button
                            onClick={() => goToPage(page as number)}
                            className={`min-w-[40px] px-3 py-2 text-sm font-medium border rounded-lg transition-all ${
                              currentPage === page
                                ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                                : 'bg-white text-gray-700 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600'
                            }`}
                          >
                            {page}
                          </button>
                        )}
                      </span>
                    ))}
                  </div>

                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border rounded-lg transition-all ${
                      currentPage === totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600'
                    }`}
                  >
                    Siguiente
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                {/* Mobile Page Selector */}
                <div className="sm:hidden">
                  <select
                    value={currentPage}
                    onChange={(e) => goToPage(parseInt(e.target.value))}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <option key={page} value={page}>
                        Página {page} de {totalPages}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
